import React, { useState, useEffect, useCallback } from 'react';
import styles from './ExpressionTextarea.module.css';

export interface ExpressionTextareaProps {
  id?: string;
  value: string;
  onChange: (newValue: string, isValidJson: boolean, parsedValue?: any) => void;
  disabled?: boolean;
  className?: string;
  textareaClassName?: string;
  errorClassName?: string;
  placeholder?: string;
  rows?: number;
  availableKeys?: string[];
  showAvailableKeysButton?: boolean;
  showSyntaxValidation?: boolean;
}

export const ExpressionTextarea: React.FC<ExpressionTextareaProps> = ({
  id,
  value,
  onChange,
  disabled = false,
  className = '',
  textareaClassName = '',
  errorClassName = '',
  placeholder,
  rows = 3,
  availableKeys,
  showAvailableKeysButton = false,
  showSyntaxValidation = true,
}) => {
  const [inputValue, setInputValue] = useState<string>(value);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isKeysVisible, setIsKeysVisible] = useState<boolean>(false);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(event.target.value);
  };

  const handleBlur = useCallback(() => {
    if (inputValue.trim() === '') {
      onChange("", true);
      setParseError(null);
      return;
    }

    try {
      const parsed = JSON.parse(inputValue);
      onChange(inputValue, true, parsed);
      setParseError(null);
    } catch (error) {
      if (error instanceof Error) {
        setParseError(`Invalid JSON: ${error.message}`);
      } else {
        setParseError('Invalid JSON format.');
      }
      onChange(inputValue, false);
    }
  }, [inputValue, onChange]);

  const toggleKeysVisibility = () => {
    setIsKeysVisible(!isKeysVisible);
  };

  return (
    <div className={`${styles.expressionContainer} ${className}`}>
      <textarea
        id={id}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        disabled={disabled}
        className={`${styles.textarea} ${textareaClassName} ${parseError && showSyntaxValidation ? styles.textareaError : ''}`}
        placeholder={placeholder}
        rows={rows}
      />
      {showSyntaxValidation && parseError && (
        <div className={`${styles.errorMessage} ${errorClassName}`}>
          {parseError}
        </div>
      )}
      {availableKeys && availableKeys.length > 0 && showAvailableKeysButton && (
        <div className={styles.availableKeysContainer}>
          <button
            type="button"
            onClick={toggleKeysVisibility}
            className={styles.toggleKeysButton}
          >
            {isKeysVisible ? 'Hide Available Keys' : 'Show Available Keys'}
          </button>
          {isKeysVisible && (
            <ul>
              {availableKeys.map((key) => (
                <li key={key}>{key}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};